import * as React from "react";
import Flexbox from "flexbox-react";
import { InputGroup, Spinner } from "@blueprintjs/core";
import axios from "axios";
import Cookies from "js-cookie";
import { observer, Provider } from "mobx-react";
import ThemeWrapper from "../components/styles/ThemeWrapper/index.js";
import ErrorBoundary from "../components/errors/ErrorBoundary";
import ScrollableDropdown from "../components/shared/ScrollableDropdown";
import ToastStore from "../stores/ToastStore";
import Utilities from "../utils/Utilities.js";

import Feedback360ReportTable from "../components/feedback_360_reports/Feedback360ReportTable";
import '../components/feedback_360_reports/Feedback360ReportView.css'

export default class Review360ReportView extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            searchQuery: "",

            reviewData: [],
            isLoading: false,

            currentPage: 1,
            perPage: 10,
            totalCount: 0,
            totalPages: 0,

            goalCycleList: [],
            selectedTimeframe: {},

            profiles: [],
            selectedProfile: null,

            filters: {
                status: "all"
            },

            isLoadingCycles: false,
            isLoadingProfiles: false
        };
    }

    componentDidMount() {
        Promise.all([
            this.getAllGoalCycleList()
        ]).then(() => {
            this.fetch360ReviewReport();
        });
    }

    getAllGoalCycleList = () => {
        this.setState({ isLoadingCycles: true });

        return axios
            .get(`${Cookies.get("apiEnv")}/goal_settings/goal_cycle_list`)
            .then(response => {
                const goalCycleList = response.data || [];

                this.setState({
                    goalCycleList,
                    selectedTimeframe: goalCycleList[0] || {},
                    isLoadingCycles: false
                });
            })
            .catch(error => {
                console.error("Failed to load goal cycles", error);
                this.setState({
                    goalCycleList: [],
                    selectedTimeframe: {},
                    isLoadingCycles: false
                });
            });
    };


    fetch360ReviewReport = (page = this.state.currentPage, perPage = this.state.perPage) => {
        const { selectedTimeframe, filters, searchQuery } = this.state;

        if (!selectedTimeframe?.id) { return; }
        this.setState({ isLoading: true });

        const params = {
            goal_setting_id: selectedTimeframe.id,
            page,
            per_page: perPage,

            ...(filters.status !== "all"
                ? { status: filters.status } : {}),
            ...(searchQuery?.trim()
                ? { search: searchQuery.trim() } : {})
        };

        axios.get(`${Cookies.get("apiEnv")}/feedback_reports/report_table/`, { params })
            .then(response => {
                const apiData = response?.data || {};
                const data = apiData?.data || [];

                const formattedData = data.map(item => ({
                    id: item?.user.id || item?.id,
                    userId: item?.user.id || item?.id,
                    name: item?.user?.full_name || "",
                    email: item?.user?.email,
                    imageFileName: item?.user?.image_file_name || null,
                    selfRating: item?.self_rating || "-",
                    otherRating: item?.others_rating || "-",
                    status: item?.status || "-",
                    release: Number(item?.release) === 1 ? 1 : 0

                }));

                const meta = apiData?.meta || {};
                const totalCount = Number.isFinite(meta.total_count)
                    ? meta.total_count : formattedData.length;
                const pageValue = Number.isFinite(meta.page)
                    ? meta.page : page;
                const perPageValue = Number.isFinite(meta.per_page)
                    ? meta.per_page : perPage;
                const totalPages = Number.isFinite(meta.total_pages)
                    ? meta.total_pages : Math.max(1, Math.ceil(totalCount / perPageValue));

                this.setState({
                    reviewData: formattedData,
                    totalCount,
                    totalPages,
                    currentPage: pageValue,
                    perPage: perPageValue,
                    isLoading: false
                });
            })
            .catch(error => {
                console.error("Failed to load 360 performance review report", error);
                this.setState({
                    reviewData: [],
                    totalCount: 0,
                    totalPages: 0,
                    isLoading: false
                });
            });
    };

    handleSelectGoalCycle = timeframe => {
        this.setState(
            {
                selectedTimeframe: timeframe,
                currentPage: 1
            },
            () => {
                this.fetch360ReviewReport(1, this.state.perPage);
            }
        );
    };

    handleSelectProfile = profile => {
        this.setState(
            {
                selectedProfile: profile,
                currentPage: 1
            },
            () => {
                this.fetch360ReviewReport(1, this.state.perPage);
            }
        );
    };

    handleSelectStatus = status => {
        this.setState(
            {
                filters: { ...this.state.filters, status },
                currentPage: 1
            },
            () => {
                this.fetch360ReviewReport(1, this.state.perPage);
            }
        );
    };

    handleSearchChange = event => {
        const value = event.target.value;
        this.setState({ searchQuery: value });

        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => {
            this.setState(
                {
                    currentPage: 1
                },
                () => {
                    this.fetch360ReviewReport(
                        1,
                        this.state.perPage
                    );
                }
            );
        }, 400);
    };

    handlePageChange = pageNumber => {
        this.setState(
            {
                currentPage: pageNumber
            },
            () => {
                this.fetch360ReviewReport(
                    pageNumber,
                    this.state.perPage
                );
            }
        );
    };

    handlePerPageChange = event => {
        const perPage = parseInt(event.target.value, 10) || 10;

        this.setState(
            {
                perPage,
                currentPage: 1
            },
            () => {
                this.fetch360ReviewReport(1, perPage);
            }
        );
    };
    handleRelease = async (row) => {
        const { selectedTimeframe } = this.state;

        const userId = row?.userId || row?.id;
        const goalSettingId = selectedTimeframe?.id;

        if (!userId || !goalSettingId) {
            console.error("Missing userId or goalSettingId", { row, selectedTimeframe });
            return;
        }

        try {
            await axios.get( `${Cookies.get("apiEnv")}/feedback_reports/ai_report`,
                {
                    params: {
                        goal_setting_id: goalSettingId,
                        user_id: userId
                    }
                }
            );
            // Refresh table after successful API call
            this.fetch360ReviewReport(
                this.state.currentPage,
                this.state.perPage
            );

        } catch (error) {
            console.error("Failed to release report:", error);
        }
    };


    handleRowClick = row => {
        const { selectedTimeframe } = this.state;

        const userId = row.userId || row.id;
        const goalSettingId = selectedTimeframe?.id;

        if (!userId || !goalSettingId) {
            ToastStore.showToast("Missing userId or goalSettingId", "danger")
            return;
        }

        const payload = {
            u: userId,
            g: goalSettingId
        };

        const encoded = btoa(JSON.stringify(payload));

        Cookies.set("selectedEmployeeId", userId);
        Cookies.set("selectedEmployeeName", row.name);
        Cookies.set("selectedGoalCycleId", goalSettingId);

        sessionStorage.setItem("team_goals_context", JSON.stringify({
            userId: userId,
            goalSettingId: goalSettingId
        }));

        Utilities.navigate(`/360_feedback/report/employee?r=${encodeURIComponent(encoded)}`);
    };

    render() {
        const {
            searchQuery,
            reviewData,
            isLoading,
            currentPage,
            perPage,
            totalCount,
            totalPages,
            goalCycleList,
            selectedTimeframe,
            profiles,
            selectedProfile,
            filters
        } = this.state;

        return (
            <Provider>
                <ThemeWrapper>
                    <ErrorBoundary>
                        <Flexbox  flexGrow={1}  width="100%" alignContent="stretch" flexDirection="column"  paddingBottom="0px" >
                            <Flexbox
                                flexGrow={1}
                                flexDirection="column"
                                className="fixed-width-container"
                                marginTop="20px"
                            >
                                {/* Header */}
                                <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: "15px",
                                        marginBottom: "25px"
                                    }}
                                >
                                    <h1 style={{
                                            fontSize: "24px",
                                            margin: 0,
                                            color: "#172b4d"
                                        }}
                                    >
                                        360 Performance Review
                                    </h1>

                                    {/* Filters */}
                                    <Flexbox
                                        alignItems="center"
                                        flexWrap="wrap"
                                        style={{
                                            gap: "12px"
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "240px"
                                            }}
                                        >
                                            <ScrollableDropdown
                                                width={240}
                                                label={ selectedTimeframe?.title || "Modification cycle"}
                                                items={goalCycleList}
                                                getKey={item => item.id}
                                                getLabel={item => item.title}
                                                enableSearch
                                                onSelect={ this.handleSelectGoalCycle }
                                            />
                                        </div>
                                        {/* <div
                                            style={{
                                                width: "220px"
                                            }}
                                        >
                                            <InputGroup
                                                leftIcon="search"
                                                placeholder="Search by users name..."
                                                value={searchQuery}
                                                large
                                                onChange={
                                                    this.handleSearchChange
                                                }
                                                style={{
                                                    width: "100%",
                                                    borderRadius: "20px",
                                                    fontSize: "14px"
                                                }}
                                                inputProps={{
                                                    style: {
                                                        borderRadius:
                                                            "20px"
                                                    }
                                                }}
                                            />
                                        </div> */}
                                    </Flexbox>
                                </div>

                                {/* Total Employees */}
                                <Flexbox alignItems="center" marginBottom="25px" >
                                    <div>
                                    </div>
                                    <span style={{ color: "#4d4d4d" }}>
                                        Total Employees
                                    </span>
                                    <span style={{ fontWeight: 600,   marginLeft: "10px" }} >
                                        {totalCount}
                                    </span>
                                </Flexbox>

                                {/* TABLE COMPONENT */}
                                <div className="feedback-status-legend">
                                    <div className="feedback-status-item">
                                        <span className="status-dot not-started"></span>
                                        <div>
                                            <strong>Not Started</strong>
                                            <p>Feedback has not been started yet.</p>
                                        </div>
                                    </div>
                                    <div className="feedback-status-item">
                                        <span className="status-dot in-progress"></span>
                                        <div>
                                            <strong>In Progress</strong>
                                            <p>Feedback is currently in progress and has not yet been completed.</p>
                                        </div>
                                    </div>
                                    <div className="feedback-status-item">
                                        <span className="status-dot completed"></span>
                                        <div>
                                            <strong>Completed</strong>
                                            <p>Feedback has been submitted successfully.</p>
                                        </div>
                                    </div>
                                </div>

                                <Feedback360ReportTable
                                    data={reviewData}
                                    isLoading={isLoading}
                                    currentPage={currentPage}
                                    perPage={perPage}
                                    totalCount={totalCount}
                                    totalPages={totalPages}
                                    onPageChange={this.handlePageChange}
                                    onPerPageChange={this.handlePerPageChange}
                                    onRowClick={this.handleRowClick}
                                    onRelease={this.handleRelease}
                                />

                            </Flexbox>
                        </Flexbox>
                    </ErrorBoundary>
                </ThemeWrapper>
            </Provider>
        );
    }
}